import { useCallback } from "react";
import { useSelector } from "react-redux";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { QueryLoggingItem, ReportField, ValueCell } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectCurrentReportId } from "../../../../store/selectors/currentReportSelectors";
import {
  createCurrentReportAction,
  createDevToolsAction,
  createDrillDownPageAction,
} from "../../../../store/utils/storeUtils";
import { isPersistedAggregation } from "../../../../utilities/fieldsHelper.ts";
import { getFormulaPreview } from "../drillDown/utils/formulaPreviewHelper.ts";

const useCommonReportHandlers = (aggregationFields: ReportField[]) => {
  const dispatch = useAppDispatch();
  const currentReportId = useSelector(selectCurrentReportId);

  const setEtag = useCallback(
    (etag: string | undefined, cacheValid: boolean | undefined) => {
      dispatch(createCurrentReportAction("setEtag", { etag, cacheValid }));
    },
    [dispatch]
  );

  const onDrillDown = useCallback(
    (field: ReportField, cell: ValueCell, options?: { reportId?: string }) => {
      const reportId = options?.reportId;

      if (!reportId && !currentReportId) {
        return;
      }

      const drillDownId = cell.drillDownId;
      if (!drillDownId) {
        return;
      }

      const isCustomColumn = !!field.config.customId;
      const formulaPreview = isCustomColumn ? (getFormulaPreview(field, aggregationFields) ?? "") : undefined;

      dispatch(
        createDrillDownPageAction("add", {
          pageId: generateGuid(),
          reportId: currentReportId ?? reportId ?? "",
          onlyLedger: false,
          chartOfAccounts: !isPersistedAggregation(field),
          layer: {
            measure: field,
            cell,
            drillDownId,
            formulaPreview,
            aggregationFields: aggregationFields,
          },
        })
      );
    },
    [aggregationFields, currentReportId, dispatch]
  );

  const updateDevTools = useCallback(
    (loggingItems: QueryLoggingItem[] | undefined) => {
      dispatch(createDevToolsAction("update", { loggingItems }));
    },
    [dispatch]
  );

  return {
    setEtag,
    updateDevTools,
    onDrillDown,
  };
};

export default useCommonReportHandlers;
